import {
  AnyAction,
  createSlice,
  CreateSliceOptions,
  PayloadAction,
  SliceCaseReducers,
  ValidateSliceCaseReducers,
} from "@reduxjs/toolkit";
import {
  allOnboardingFulfilledMatchers,
  allOnboardingPendingMatchers,
  allOnboardingRejectedMatchers,
} from "../../api/onboardingApi";
import {
  allResumeFulfilledMatchers,
  allResumePendingMatchers,
  allResumeRejectedMatchers,
} from "../../api/resumeApi";
import {
  allStatusFulfilledMatchers,
  allStatusPendingMatchers,
  allStatusRejectedMatchers,
} from "../../api/statusApi";
import { ScaErrorType } from "../../providers";
import { PageContent, RequestError } from "../../types";
import {
  buildJsonFromValues,
  REDIRECT_ERROR_CODE,
  SCA_TRANSMIT_ERROR_CODE,
} from "../../utils";
import { OnboardingState } from "../onboarding";

export const createGenericState = <
  TState extends OnboardingState,
  Reducers extends SliceCaseReducers<TState>,
>(
  name: string,
  initialState: TState,
  reducers?: {
    reducers?: ValidateSliceCaseReducers<TState, Reducers>;
    extraReducers?: CreateSliceOptions["extraReducers"];
  },
) => {
  const slice = createSlice({
    name,
    initialState,
    reducers: {
      reset() {
        return initialState;
      },
      setLoading(state, action: PayloadAction<boolean>) {
        state.isLoading = action.payload;
      },
      ...reducers?.reducers,
    },
    extraReducers: reducers?.extraReducers,
  });

  return slice;
};

export const onPendingMatcher = <TState extends OnboardingState>(
  state: TState,
  action: AnyAction,
) => {
  const lastAction = action.meta.arg.endpointName;
  state.errorCode = undefined;
  state.lastAction = lastAction;
  state.currentStep = action.meta.arg.originalArgs.stepId || -1;
  state.isLoading = true;
  state.isError = false;
  state.isUninitialized = false;
  state.isSuccess = false;
  if (lastAction === "start") {
    state.data = undefined;
    state.stepValues = undefined;
    state.processId = undefined;
  }
  return state;
};

export const onRejectMatcher = <TState extends OnboardingState>(
  state: TState,
  action: AnyAction,
) => {
  const { payload: rawPayload = {} } = action;
  const payload = rawPayload as Record<string, unknown>;
  state.isError = true;
  state.errorCode = payload?.status as number;
  state.redirectError = (payload as RequestError).redirectError;
  if (payload?.status === SCA_TRANSMIT_ERROR_CODE) {
    state.error = payload?.data as ScaErrorType;
  }
  if (state.redirectError) {
    if (Number(payload.status) !== REDIRECT_ERROR_CODE) {
      state.isLoading = false;
    }
  } else {
    state.isLoading = false;
  }
  if (state.data && state.stepValues) {
    state.data = buildJsonFromValues({
      pageContent: state.data,
      values: state.stepValues,
    });
  }
};

export const onFulfilledMatcher = <TState extends OnboardingState>(
  state: TState,
  action: PayloadAction<unknown, string, Record<string, unknown>>,
) => {
  const { payload: rawPayload = {} } = action;
  const payload = rawPayload as Record<string, unknown>;
  state.isLoading = false;
  state.isSuccess = true;
  state.isError = false;
  state.errorCode = undefined;
  if (action.payload) {
    state.data = payload as PageContent;
    state.processId = payload.processId as string;
  }
};

export const allRejectedMatchers = [
  ...allResumeRejectedMatchers,
  ...allStatusRejectedMatchers,
  ...allOnboardingRejectedMatchers,
];

export const allPendingMatchers = [
  ...allResumePendingMatchers,
  ...allStatusPendingMatchers,
  ...allOnboardingPendingMatchers,
];

export const allFulfilledMatchers = [
  ...allOnboardingFulfilledMatchers,
  ...allResumeFulfilledMatchers,
  ...allStatusFulfilledMatchers,
];
